import {
  extractTextFromField,
  generateSEOMetadata,
  getPageTextContent,
  invalidateDocumentListData,
  isPageContentRelevantForAISeo,
  metaDescriptionProperty,
  metaTitleProperty,
  type Role,
} from "@prismicio/editor-fields"
import { Button, ButtonGroup, Tooltip, useAlert } from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import { useState, useTransition } from "react"

import { staticEditorConfig } from "../../../app/editorConfig"
import { useAiSeoMetadataFlag } from "../../../app/integrations/AmplitudeExperiments"
import { publishDocument } from "../../../app/services/core"
import {
  getReleaseId,
  invalidateReleaseSizeData,
  publishReleaseNow,
} from "../../../app/services/release"
import { useSharedOnboarding } from "../../../app/services/sharedOnboarding"
import { trackSeoModalOpen } from "../../../app/services/tracking"
import { useRole } from "../../../app/services/user"
import { revalidateDocumentMeta } from "../../documentData"
import { useExistingDocument } from "../../DocumentProvider"
import { validateUID } from "../validateUID"
import { actionAlert, type PendingActionProps } from "./HeaderButtons"
import {
  MetadataFieldGenerationModal,
  type MetadataFieldsData,
} from "./MetadataFieldGenerationModal"
import { ReleaseButton } from "./ReleaseButton"

export function PublishButton(props: PendingActionProps) {
  const { pendingAction, onPendingActionChange } = props
  const { content, data, locale, documentId } = useExistingDocument()
  const { customType } = data
  const [metadataFieldsData, setMetadataFieldsData] = useState<
    MetadataFieldsData | undefined
  >()

  const { disabled, publish, roleCanPublish, tooltip } = usePublishButton(props)
  const { completeStep } = useSharedOnboarding()
  const aiSeoMetadataFlag = useAiSeoMetadataFlag()

  const baseTrackingPayload = {
    documentId,
    locale,
  }

  async function openMetadataFieldGenerationModal(
    pageTextContent: string,
    existingMetaTitle?: string,
    existingMetaDescription?: string,
  ) {
    const { documentBaseUrl, authStrategy, repository } = staticEditorConfig
    if (aiSeoMetadataFlag === "off") return

    onPendingActionChange("publish")

    try {
      const suggestion = await generateSEOMetadata(
        repository,
        documentBaseUrl,
        authStrategy,
        {
          pageTextContent,
          contentLocale: locale,
        },
      )
      onPendingActionChange(undefined)

      setMetadataFieldsData({
        metaTitle: {
          value: existingMetaTitle || suggestion[metaTitleProperty],
          isSuggestion: !existingMetaTitle,
        },
        metaDescription: {
          value: existingMetaDescription || suggestion[metaDescriptionProperty],
          isSuggestion: !existingMetaDescription,
        },
      })

      trackSeoModalOpen(baseTrackingPayload)
    } catch (error) {
      // if generation fails, publish
      void publish()
      void completeStep("createContent")
    }
  }

  async function onClickPublish() {
    const pageTextContent = getPageTextContent(content, customType)

    const existingMetaTitle = extractTextFromField(content[metaTitleProperty])
    const existingMetaDescription = extractTextFromField(
      content[metaDescriptionProperty],
    )

    const hasMetadata = Boolean(existingMetaTitle && existingMetaDescription)
    const isContentSeoRelevant = isPageContentRelevantForAISeo(pageTextContent)
    const isAiSeoMetadataEnabled = aiSeoMetadataFlag !== "off"

    if (isAiSeoMetadataEnabled && !hasMetadata && isContentSeoRelevant) {
      void openMetadataFieldGenerationModal(
        pageTextContent,
        existingMetaTitle,
        existingMetaDescription,
      )
      return
    }

    await publish()
    void completeStep("createContent")
  }

  const publishButton = (
    <Tooltip
      visible={!roleCanPublish}
      title={tooltip.title}
      content={tooltip.content}
    >
      <Button
        size="large"
        loading={pendingAction === "publish"}
        disabled={disabled}
        onClick={onClickPublish}
      >
        Publish
      </Button>
    </Tooltip>
  )

  return (
    <>
      <ButtonGroup density="compact" size="large">
        {publishButton}
        <ReleaseButton {...props} />
      </ButtonGroup>

      {metadataFieldsData && (
        <MetadataFieldGenerationModal
          initialPageMetadata={metadataFieldsData}
          onOpenChange={(open) => {
            if (!open) {
              onPendingActionChange(undefined)
              setMetadataFieldsData(undefined)
            }
          }}
          publish={publish}
          onPendingActionChange={props.onPendingActionChange}
          baseTrackingPayload={baseTrackingPayload}
        />
      )}
    </>
  )
}

function usePublishButton(props: PendingActionProps) {
  const { pendingAction, onPendingActionChange } = props
  const { data, edited, content, locale } = useExistingDocument()

  const { meta, version, customType } = data

  const { showAlert } = useAlert()
  const [isUpdating, startTransition] = useTransition()

  const queryClient = useQueryClient()

  const { role } = useRole(locale)
  const roleCanPublish = publishPermission[role]

  const disabled =
    edited ||
    version.status === "archived" ||
    Boolean(pendingAction) ||
    isUpdating ||
    !roleCanPublish

  async function publish() {
    onPendingActionChange("publish")

    const isValidUID = await validateUID({
      content,
      documentId: meta.id,
      languageId: meta.language.id,
      customType,
      showAlert,
      pendingAction: "publish",
      onRetry: publish,
    })

    if (!isValidUID) return onPendingActionChange(undefined)

    const releaseId = getReleaseId(version)

    const publishAction = releaseId
      ? publishReleaseNow(meta.id, releaseId)
      : publishDocument(meta.id)

    publishAction
      .then(() => {
        startTransition(() => void revalidateDocumentMeta(meta.id))

        if (releaseId) {
          // When publishing now a document already in a release, that particular release will not contain the document anymore => releases's size will change
          void invalidateReleaseSizeData(releaseId)
        }

        void invalidateDocumentListData(queryClient)
      })
      .catch(() =>
        actionAlert({ action: "publish", showAlert, onRetry: publish }),
      )
      .finally(() => {
        onPendingActionChange(undefined)
      })
  }

  return {
    disabled,
    publish,
    roleCanPublish,
    tooltip: getTooltip(role),
  }
}

const publishPermission: Record<Role, boolean> = {
  SuperUser: true,
  Owner: true,
  Administrator: true,
  Publisher: true,
  Writer: false,
  Contributor: false,
}

function getTooltip(role: Role) {
  if (role === "Contributor")
    return {
      title: "You can't update documents",
      content:
        "Only users with the role of publisher or administrator can update documents.",
    }

  return {
    title: "You can't publish",
    content:
      "Only users with the role of publisher or administrator can publish documents.",
  }
}
